import { useState, useEffect, useCallback, useMemo } from "react";
import { useBoardIntegrations } from "frontend/hooks/use-board-integrations";
import { MondayColumn } from "shared/integrations/mapper";
import {
  type BoardWithColumns,
  type BoardWithCompatibility,
} from "frontend/canvas-designer-new/monday-import-items/monday-import.hook";
import { type GanttChartColumnMapping, type GanttItemMapping, EMPTY_ITEM_MAPPING } from "./synced-gantt-chart.types";

export function ganttBoardFilter(board: BoardWithColumns): boolean {
  const columns = board.columns ?? [];
  const hasTimeline = columns.some((col) => col.type === "timeline");
  const dateColumnsCount = columns.filter((col) => col.type === "date").length;
  return hasTimeline || dateColumnsCount >= 2;
}

function getFilteredAndSortedColumns(
  columns: MondayColumn[],
  validTypes: readonly string[],
  selectedId: string | null
): MondayColumn[] {
  const enabledColumns = columns.filter((col) => validTypes.includes(col.type));
  const disabledColumns = columns.filter((col) => !validTypes.includes(col.type));

  const sortWithSelected = (cols: MondayColumn[]) =>
    cols.sort((a, b) => {
      if (a.id === selectedId) return -1;
      if (b.id === selectedId) return 1;
      return a.title.localeCompare(b.title);
    });

  return [...sortWithSelected(enabledColumns), ...sortWithSelected(disabledColumns)];
}

interface UseSyncedGanttChartParams {
  documentId: string | undefined;
  selectedBoard: (BoardWithCompatibility & BoardWithColumns) | null;
}

export function useSyncedGanttChart({ documentId, selectedBoard }: UseSyncedGanttChartParams) {
  const { mondayIntegration, addBoardIntegration } = useBoardIntegrations(documentId);

  const [columnMapping, setColumnMapping] = useState<GanttChartColumnMapping>({
    items: { ...EMPTY_ITEM_MAPPING },
    subItems: { ...EMPTY_ITEM_MAPPING },
  });

  const [dependencyEnabled, setDependencyEnabled] = useState(false);

  const columns: MondayColumn[] = useMemo(() => {
    const boardColumns = selectedBoard?.columns;
    if (!boardColumns) return [];
    return boardColumns.map((c) => ({
      id: c.id ?? "",
      type: c.type ?? "",
      title: c.title ?? "",
      settings: c.settings_str ? JSON.parse(c.settings_str) : {},
    }));
  }, [selectedBoard]);

  const subItemsColumns: MondayColumn[] = useMemo(() => {
    const subitemBoard = (
      selectedBoard as {
        subitem_board?: {
          id: string;
          columns?: Array<{ id: string; type?: string; title?: string; settings_str?: string }>;
        };
      }
    )?.subitem_board;
    const subItemsBoardColumns = subitemBoard?.columns;
    if (!subItemsBoardColumns || subItemsBoardColumns.length === 0) return [];
    return subItemsBoardColumns.map((c) => ({
      id: c.id ?? "",
      type: c.type ?? "",
      title: c.title ?? "",
      settings: c.settings_str ? JSON.parse(c.settings_str) : {},
    }));
  }, [selectedBoard]);

  useEffect(() => {
    if (!selectedBoard) {
      setColumnMapping({
        items: { ...EMPTY_ITEM_MAPPING },
        subItems: { ...EMPTY_ITEM_MAPPING },
      });
      setDependencyEnabled(false);
      return;
    }

    const hasDependencyColumn = columns.some((col) => col.type === "dependency");
    const dependencyColumn = columns.find((col) => col.type === "dependency");
    const timelineColumn = columns.find((col) => col.type === "timeline");
    const dateColumns = columns.filter((col) => col.type === "date");

    // Use sub-items board columns if available, otherwise fall back to main board columns
    const columnsForSubItems = subItemsColumns.length > 0 ? subItemsColumns : columns;
    const hasSubItemsDependencyColumn = columnsForSubItems.some((col) => col.type === "dependency");
    const subItemsDependencyColumn = columnsForSubItems.find((col) => col.type === "dependency");
    const subItemsTimelineColumn = columnsForSubItems.find((col) => col.type === "timeline");
    const subItemsDateColumns = columnsForSubItems.filter((col) => col.type === "date");

    // Auto-select columns based on what's available
    const baseItemsMapping: GanttItemMapping = { ...EMPTY_ITEM_MAPPING };
    const baseSubItemsMapping: GanttItemMapping = { ...EMPTY_ITEM_MAPPING };

    if (timelineColumn) {
      baseItemsMapping.timeline = timelineColumn.id;
    } else if (dateColumns.length >= 2 && !timelineColumn) {
      baseItemsMapping.startDate = dateColumns[0].id;
      baseItemsMapping.endDate = dateColumns[1].id;
    }

    if (subItemsTimelineColumn) {
      baseSubItemsMapping.timeline = subItemsTimelineColumn.id;
    } else if (subItemsDateColumns.length >= 2 && !subItemsTimelineColumn) {
      baseSubItemsMapping.startDate = subItemsDateColumns[0].id;
      baseSubItemsMapping.endDate = subItemsDateColumns[1].id;
    }

    if (hasDependencyColumn && dependencyColumn) {
      baseItemsMapping.dependencyEnabled = true;
      baseItemsMapping.dependency = dependencyColumn.id;
    }

    if (hasSubItemsDependencyColumn && subItemsDependencyColumn) {
      baseSubItemsMapping.dependencyEnabled = true;
      baseSubItemsMapping.dependency = subItemsDependencyColumn.id;
    }

    setDependencyEnabled(hasDependencyColumn);

    setColumnMapping({
      items: baseItemsMapping,
      subItems: baseSubItemsMapping,
    });
  }, [selectedBoard, columns, subItemsColumns]);

  const handleItemMappingChange = useCallback(
    (
      mapTarget: "items" | "subItems",
      field: keyof Omit<GanttItemMapping, "dependencyEnabled">,
      value: string | null
    ) => {
      setColumnMapping((previous) => {
        const targetMapping = previous[mapTarget];
        const newMapping = { ...targetMapping, [field]: value };

        // If selecting a timeline, clear start/end dates
        if (field === "timeline" && value) {
          newMapping.startDate = null;
          newMapping.endDate = null;
        }

        // If selecting a start date, clear timeline
        if (field === "startDate" && value) {
          newMapping.timeline = null;
        }

        // If selecting an end date, clear timeline
        if (field === "endDate" && value) {
          newMapping.timeline = null;
        }

        return {
          ...previous,
          [mapTarget]: newMapping,
        };
      });
    },
    []
  );

  // Global dependency toggle that applies to both items and subitems
  const handleGlobalDependencyToggle = useCallback(
    (enabled: boolean) => {
      setDependencyEnabled(enabled);

      const dependencyColumn = columns.find((col) => col.type === "dependency");
      const dependencyId = enabled && dependencyColumn ? dependencyColumn.id : null;

      const columnsForSubItems = subItemsColumns.length > 0 ? subItemsColumns : columns;
      const subItemsDependencyColumn = columnsForSubItems.find((col) => col.type === "dependency");
      const subItemsDependencyId = enabled && subItemsDependencyColumn ? subItemsDependencyColumn.id : null;

      setColumnMapping((previous) => ({
        ...previous,
        items: {
          ...previous.items,
          dependencyEnabled: enabled,
          dependency: dependencyId,
        },
        subItems: {
          ...previous.subItems,
          dependencyEnabled: enabled,
          dependency: subItemsDependencyId,
        },
      }));
    },
    [columns, subItemsColumns]
  );

  // Check if items mapping is valid (has timeline OR at least start date)
  const isItemsMappingValid = columnMapping.items.timeline !== null || columnMapping.items.startDate !== null;

  // Sub-items mapping is optional, but if started must be complete
  const isSubItemsMappingValid =
    columnMapping.subItems.timeline === null &&
    columnMapping.subItems.startDate === null &&
    columnMapping.subItems.endDate === null
      ? true // No sub-items mapping started = valid
      : columnMapping.subItems.timeline !== null || columnMapping.subItems.startDate !== null;

  const isNextDisabled = !selectedBoard || !isItemsMappingValid || !isSubItemsMappingValid || !mondayIntegration;

  const getColumnsForField = useCallback(
    (validTypes: readonly string[], selectedId: string | null, forSubItems: boolean = false) => {
      const columnsToUse = forSubItems && subItemsColumns.length > 0 ? subItemsColumns : columns;
      return getFilteredAndSortedColumns(columnsToUse, validTypes, selectedId);
    },
    [columns, subItemsColumns]
  );

  const isColumnEnabled = useCallback(
    (columnId: string, validTypes: readonly string[], forSubItems: boolean = false) => {
      const columnsToUse = forSubItems && subItemsColumns.length > 0 ? subItemsColumns : columns;
      const column = columnsToUse.find((col) => col.id === columnId);
      return column ? validTypes.includes(column.type) : false;
    },
    [columns, subItemsColumns]
  );

  // Check available column types
  const hasTimelineColumns = columns.some((col) => col.type === "timeline");
  const hasDateColumns = columns.filter((col) => col.type === "date").length >= 2;
  const hasDependencyColumns = columns.some((col) => col.type === "dependency");

  // Check if user selected a date column (to show end date selector)
  const itemsUsesDateColumns = columnMapping.items.startDate !== null;
  const subItemsUsesDateColumns = columnMapping.subItems.startDate !== null;

  return {
    mondayIntegration,
    addBoardIntegration,
    columns,
    subItemsColumns: subItemsColumns.length > 0 ? subItemsColumns : columns,
    columnMapping,
    handleItemMappingChange,
    handleGlobalDependencyToggle,
    dependencyEnabled,
    isNextDisabled,
    getColumnsForField,
    isColumnEnabled,
    hasTimelineColumns,
    hasDateColumns,
    hasDependencyColumns,
    itemsUsesDateColumns,
    subItemsUsesDateColumns,
  };
}
